import { Component, forwardRef, Input, Output, EventEmitter, ViewChild } from '@angular/core'
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ValidationErrors,
  AbstractControl,
} from '@angular/forms'
import { DateInputComponent } from '../date-input/date-input.component'
import { TimeInputComponent } from '../time-input/time-input.component'
import { DateTime } from '../../models/DateTime/DateTime'
import { DateSegment } from '../../models/DateTime/DateSegment'
import { TimeSegment } from '../../models/DateTime/TimeSegment'

@Component({
  selector: 'app-date-time-input',
  templateUrl: './date-time-input.component.html',
  styleUrls: ['./date-time-input.component.scss'],
  host: {
    '[class.disabled]': 'disabled',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true,
    },
  ],
  imports: [DateInputComponent, TimeInputComponent],
})
export class DateTimeInputComponent implements ControlValueAccessor {
  @Input() value: DateTime = new DateTime(null, null)
  @Input() disabled: boolean = false

  @Output() changed = new EventEmitter<DateTime>()
  @Output() dateChanged = new EventEmitter<DateSegment>()
  @Output() timeChanged = new EventEmitter<TimeSegment>()

  @ViewChild('dateInputComponent', { static: false }) dateComponent: DateInputComponent
  @ViewChild('timeInputComponent', { static: false }) timeComponent: TimeInputComponent

  private onChange = (value: DateTime) => {}
  protected onTouched: (_: any) => void = () => {}

  // Internal DateTime object for picker binding
  public _dateTime: DateTime = new DateTime(null, null)

  constructor() {}

  writeValue(value: DateTime | null | undefined) {
    this.value = value || new DateTime(null, null)
    this._dateTime = this.value
  }

  registerOnChange(fn: (value: DateTime) => void) {
    this.onChange = fn
  }

  registerOnTouched(fn: (_: any) => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled
    if (this.dateComponent) {
      this.dateComponent.disabled = isDisabled
    }
    if (this.timeComponent) {
      this.timeComponent.disabled = isDisabled
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    return null
  }

  handleDateChange() {
    this._dateTime = new DateTime(this.dateComponent.value, this._dateTime?.time ?? null)
    this.emitValue()
    this.dateChanged.emit(this.dateComponent.value)
  }

  handleTimeChange() {
    this._dateTime = new DateTime(this._dateTime?.date ?? null, this.timeComponent.value)
    this.emitValue()
    this.timeChanged.emit(this.timeComponent.value)
  }

  private emitValue() {
    // Always emit DateTime object (even if incomplete)
    this.value = this._dateTime
    this.onChange(this._dateTime)
    this.changed.emit(this._dateTime)
  }
}
